import { primitiveTokens } from "@appretio/ui-kit";
import type { AgSunburstSeriesOptions } from "ag-charts-enterprise";
import { useMemo } from "react";
import tinycolor from "tinycolor2";
import {
  ColourMetrics,
  GroupedColourMetrics,
  KeysWithValsOfType
} from "../../../../pages/inSeasonReviewPage/hooks/useFetchColours";

/**
 * Basic colour name to hex code map for the parent levels in the colour sunburst chart
 */
const PARENT_COLOUR_MAP: Record<string, string> = {
  GREEN: "#008000",
  YELLOW: "#FFFF00",
  PURPLE: "#800080",
  GREY: "#808080",
  BROWN: "#8B4513",
  RED: "#FF0000",
  WHITE: "#FFFFFF",
  BLUE: "#0000FF",
  ORANGE: "#FFA500",
  ROSE: "#FFE4E1",
  TURQUOISE: "#48D1CC",
  BLACK: "#000000",
  METAL: "#b71e1d",
  BEIGE: "#F5F5DC"
};

export interface UseColourChartOptionsProps {
  /**
   * The key of ColourMetrics to use for sizing the sectors.
   */
  valueKey: KeysWithValsOfType<ColourMetrics, number>;
}

const percentageFormatter = new Intl.NumberFormat("en-US", {
  style: "percent",
  signDisplay: "never"
});

/**
 * Custom hook to generate chart options for the colour sunburst chart.
 *
 * The colour chart will display a sunburst chart representing the distribution of colours in the dataset.
 * Depending on the valueKey prop, the size of the sectors will represent different metrics.
 */
export function useColourChartOptions({
  valueKey
}: UseColourChartOptionsProps) {
  return useMemo(() => {
    const series: AgSunburstSeriesOptions<GroupedColourMetrics>[] = [
      {
        type: "sunburst",
        labelKey: "name",
        sizeKey: valueKey,
        sectorSpacing: 0,
        highlightStyle: {
          fillOpacity: 0,
          label: {
            color: primitiveTokens.colors.neutral[900]
          },
          secondaryLabel: {
            color: primitiveTokens.colors.neutral[900]
          }
        },
        label: {
          enabled: true,
          color: primitiveTokens.colors.neutral[900],
          padding: 2,
          itemStyler: (item) =>
            tinycolor(item.datum.colour).isDark()
              ? {
                  /**
                   * TODO: Replace fill with color: "white" option when ag-charts supports it
                   * https://github.com/ag-grid/ag-charts/issues/5714
                   *
                   * https://bestjira.atlassian.net/browse/DAS-5417?atlOrigin=eyJpIjoiZDNiNzc1M2YwOGNlNDk1OWFiMGI2NDE2M2JhYzhlOWMiLCJwIjoiaiJ9
                   */
                  fill: primitiveTokens.colors.white[1000]
                }
              : undefined
        },
        itemStyler: (item) => {
          // Child nodes (leaf sectors) use their hex code color
          if (!item.datum.children) {
            return {
              fill: item.datum.colour
            };
          }

          // Parent nodes (outer ring) use the mapped parent color
          const parentColor = PARENT_COLOUR_MAP[item.datum.colour];
          if (!parentColor) {
            console.warn(
              `No colour mapping found for parent colour: ${item.datum.colour}`
            );
            return {
              fill: primitiveTokens.colors.neutral[500]
            };
          }
          return {
            fill: parentColor
          };
        },
        tooltip: {
          renderer: (item) => {
            return {
              heading: `${item.datum.name}`,
              title: `${item.datum.value}`,
              data: [
                {
                  label: "Percentage",
                  value: percentageFormatter.format(item.datum.ratio)
                },
                {
                  label: "Value",
                  value: item.datum.value.toString()
                }
              ]
            };
          }
        }
      }
    ];

    return { series: series };
  }, [valueKey]);
}
